import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';

import { SmallContext } from '../utils';

import Text from './Text';
import styles from './Search.less';

const cxb = cx.bind(styles);
/**
 * **Access:**
 * ```js
 * import { Search } from '@iss/client-react-components'
 * ```
 *
 * A styled controlled Search Input.
 *
 * Note: The statfull "uncontrolled" style of usage is not supported with this component. It
 * requires the consumer to manage the value.
 */
export default class Search extends React.Component {
  static contextType = SmallContext;

  static propTypes = {
    /** @ignore */
    className: PropTypes.string,

    /** Mark the control disabled. */
    disabled: PropTypes.bool,

    /** Mark the control as errored. */
    error: PropTypes.bool,

    /** Specifies the Hint Text when the input is empty. */
    placeholder: PropTypes.string,

    /**
     * A callback to recieve the changed value.
     * @param {string} value The new value.
     */
    onChange: PropTypes.func,

    /** Use small layout */
    small: PropTypes.bool,

    /** The value to put into the input control */
    value: PropTypes.string,
  };

  static defaultProps = {
    placeholder: 'Search',
  };

  /** @private */
  ref = React.createRef();

  /**
   * Child Reference to the Text input
   *
   * @private
   */
  get input() {
    return this.ref.current;
  }

  /**
   * Getter property (not a method) for the validity of the input
   *
   * @see Docs: {@link https://developer.mozilla.org/en-US/docs/Web/API/ValidityState ValidityState}
   * @public
   */
  get validity() {
    return (this.input || {}).validity;
  }

  get small() {
    return SmallContext.isSmall(this);
  }

  /**
   * focus the input
   *
   * @public
   * @method focus
   * @return {void}
   */
  focus() {
    if (this.input) {
      this.input.focus();
    }
  }

  /**
   * Clears the input. (by calling `onChange('')`)
   *
   * @public
   * @method clear
   * @return {void}
   */
  clear = () => {
    if (this.input) {
      this.input.clear();
    }
  };

  render() {
    const { className, ...props } = this.props;
    return <Text {...props} className={cx(cxb('search', { small: this.small }), className)} ref={this.ref} />;
  }
}
